// tabs/TitleStrategyTab.jsx
import React, { useMemo, useState, useEffect } from "react";
import { Users, Building2, ChevronRight, X, Target } from "lucide-react";

/* --- UI HELPERS --- */
const Pill = ({ children, tone = "muted" }) => (
  <span
    className={`pill ${tone}`}
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      height: 34,
      padding: "0 14px",
      borderRadius: 999,
      fontSize: 13,
      fontWeight: 600,
      color: tone === "brand" ? "#0056D2" : "#0F172A",
      background: tone === "brand" ? "rgba(0,86,210,0.08)" : "#F1F5F9",
      border: "1px solid #E2E8F0",
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </span>
);

const Modal = ({ isOpen, onClose, title, content }) => {
  if (!isOpen) return null;
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.6)",
        backdropFilter: "blur(4px)",
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 20,
          width: "100%",
          maxWidth: 500,
          padding: 24,
          boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
          }}
        >
          <h3 className="h2" style={{ margin: 0 }}>
            {title}
          </h3>
          <button
            onClick={onClose}
            style={{ border: "none", background: "none", cursor: "pointer" }}
          >
            <X size={20} />
          </button>
        </div>
        <div style={{ maxHeight: 400, overflowY: "auto" }}>{content}</div>
      </div>
    </div>
  );
};

/* --- MAIN COMPONENT --- */
export default function TitleStrategyTab() {
  const [actuals, setActuals] = useState([]);
  const [activeTitle, setActiveTitle] = useState(null);

  // 1. DATA LOADER: FSM Parser (201 Leads)
  useEffect(() => {
    fetch("/APAC_Reporting_India_SEA.csv")
      .then((res) => res.text())
      .then((text) => {
        const rows = [];
        let cells = [];
        let current = "";
        let inQuotes = false;
        for (let i = 0; i < text.length; i++) {
          const char = text[i];
          const next = text[i + 1];
          if (char === '"') {
            if (inQuotes && next === '"') {
              current += '"';
              i++;
            } else inQuotes = !inQuotes;
          } else if (!inQuotes && char === ",") {
            cells.push(current.trim());
            current = "";
          } else if (
            !inQuotes &&
            (char === "\n" || (char === "\r" && next === "\n"))
          ) {
            if (char === "\r") i++;
            cells.push(current.trim());
            rows.push(cells);
            cells = [];
            current = "";
          } else {
            current += char;
          }
        }
        if (current || cells.length > 0) {
          cells.push(current.trim());
          rows.push(cells);
        }

        const valid = rows
          .slice(1)
          .map((row) => ({
            title: row[0],
            industry: row[3],
            company: row[5],
            date: row[9],
          }))
          .filter((l) => l.date && l.date.includes("/"));
        setActuals(valid);
      });
  }, []);

  // 2. MAPPING LOGIC
  const titlePerformance = useMemo(() => {
    const map = {};
    actuals.forEach((lead) => {
      const raw = lead.title || "Unknown";
      if (!map[raw]) map[raw] = { count: 0, companies: new Set() };
      map[raw].count++;
      map[raw].companies.add(lead.company);
    });
    return map;
  }, [actuals]);

  const renderTitleRow = (strategyTitle) => {
    // Find matching titles in actuals (handling minor case/spacing variations)
    const matches = Object.entries(titlePerformance).filter(
      ([raw]) =>
        raw.toLowerCase().includes(strategyTitle.toLowerCase()) ||
        strategyTitle.toLowerCase().includes(raw.toLowerCase())
    );

    const totalCount = matches.reduce((sum, [_, data]) => sum + data.count, 0);
    const allCompanies = Array.from(
      new Set(matches.flatMap(([_, data]) => Array.from(data.companies)))
    );

    return (
      <div
        key={strategyTitle}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px 0",
          borderBottom: "1px solid #F1F5F9",
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 500, color: "#334155" }}>
          {strategyTitle}
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <span
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: totalCount > 0 ? "#0056D2" : "#94A3B8",
            }}
          >
            {totalCount} Leads
          </span>
          {totalCount > 0 && (
            <button
              onClick={() =>
                setActiveTitle({ name: strategyTitle, companies: allCompanies })
              }
              style={{
                border: "1px solid #E2E8F0",
                background: "#fff",
                borderRadius: 6,
                padding: "4px 8px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 4,
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              View Companies <ChevronRight size={12} />
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <section className="section" style={{ display: "grid", gap: 16 }}>
      <div className="chips" style={{ display: "flex", gap: 8 }}>
        <Pill>Title Strategy</Pill>
        <Pill tone="brand">Live Actuals: {actuals.length} leads</Pill>
      </div>

      {/* FINSERV SECTION */}
      <div
        className="card lg"
        style={{
          background: "#fff",
          border: "1px solid #E2E8F0",
          borderRadius: 20,
          padding: 24,
        }}
      >
        <div className="section-head" style={{ marginBottom: 20 }}>
          <h3 className="h2">Financial Services Strategy</h3>
          <Pill tone="brand">HR & L&D Focused</Pill>
        </div>

        <div className="grid md-g-2" style={{ gap: 20 }}>
          <div className="card" style={{ padding: 16, background: "#F8FAFC" }}>
            <h4
              style={{
                fontSize: 12,
                textTransform: "uppercase",
                color: "#64748B",
                marginBottom: 12,
              }}
            >
              L&D Bucket
            </h4>
            {["CLO", "SVP, L&D", "Learning", "Talent Development"].map(
              renderTitleRow
            )}
          </div>
          <div className="card" style={{ padding: 16, background: "#F8FAFC" }}>
            <h4
              style={{
                fontSize: 12,
                textTransform: "uppercase",
                color: "#64748B",
                marginBottom: 12,
              }}
            >
              HR Bucket
            </h4>
            {["CHRO", "Chief People Officer", "VP, HR", "Director, HR"].map(
              renderTitleRow
            )}
          </div>
        </div>
      </div>

      {/* TECH SECTION */}
      <div
        className="card lg"
        style={{
          background: "#fff",
          border: "1px solid #E2E8F0",
          borderRadius: 20,
          padding: 24,
        }}
      >
        <div className="section-head" style={{ marginBottom: 20 }}>
          <h3 className="h2">Technology Strategy (India)</h3>
          <Pill tone="brand">L&D + Engineering Leadership</Pill>
        </div>

        <div className="grid md-g-3" style={{ gap: 16 }}>
          <div className="card" style={{ padding: 16, background: "#F8FAFC" }}>
            <h4
              style={{
                fontSize: 12,
                textTransform: "uppercase",
                color: "#64748B",
                marginBottom: 12,
              }}
            >
              L&D
            </h4>
            {["CLO", "Learning & Development", "Head of Leadership"].map(
              renderTitleRow
            )}
          </div>
          <div className="card" style={{ padding: 16, background: "#F8FAFC" }}>
            <h4
              style={{
                fontSize: 12,
                textTransform: "uppercase",
                color: "#64748B",
                marginBottom: 12,
              }}
            >
              HR
            </h4>
            {["CHRO", "Talent"].map(renderTitleRow)}
          </div>
          <div className="card" style={{ padding: 16, background: "#F8FAFC" }}>
            <h4
              style={{
                fontSize: 12,
                textTransform: "uppercase",
                color: "#64748B",
                marginBottom: 12,
              }}
            >
              Engineering
            </h4>
            {["CTO", "Engineering", "CIO", "AI", "Cloud"].map(renderTitleRow)}
          </div>
        </div>
      </div>

      {/* MODAL FOR COMPANY LIST */}
      <Modal
        isOpen={!!activeTitle}
        onClose={() => setActiveTitle(null)}
        title={activeTitle?.name}
        content={
          <div style={{ display: "grid", gap: 10 }}>
            <div
              style={{
                fontSize: 12,
                color: "#64748B",
                marginBottom: 8,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <Building2 size={14} /> {activeTitle?.companies.length} Companies
              Engaged
            </div>
            {activeTitle?.companies.sort().map((c) => (
              <div
                key={c}
                style={{
                  padding: "10px 14px",
                  background: "#F8FAFC",
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {c}
              </div>
            ))}
          </div>
        }
      />
    </section>
  );
}
